import maplibregl from 'maplibre-gl';
import type { LayerState } from '@vitals/shared';

const SOURCE_ID = 'radar';
const LAYER_ID = 'radar';

/** Opacity for the raster radar overlay. */
const RADAR_OPACITY = 0.55;

/**
 * Build the MapLibre raster tile URL template for a RainViewer frame.
 * Uses color palette 4 (Universal Blue) and options 1_1 (smooth + snow).
 * The {z}/{x}/{y} placeholders are filled by MapLibre at render time.
 *
 * Reference: docs/reference/keyless-feeds.md §4
 *   template: {host}{path}/{size}/{z}/{x}/{y}/{color}/{options}.png
 */
export function radarTileUrl(host: string, path: string): string {
  return `${host}${path}/256/{z}/{x}/{y}/4/1_1.png`;
}

/**
 * Find the first symbol layer id in the current style so we can insert
 * the radar raster layer below it, keeping city labels on top.
 * Returns undefined if no symbol layers exist.
 */
function firstSymbolLayerId(map: maplibregl.Map): string | undefined {
  const layers = map.getStyle()?.layers;
  if (!layers) return undefined;
  return layers.find((l) => l.type === 'symbol')?.id;
}

/**
 * Remove the radar source and layer from the map if they exist.
 * Safe to call even when neither is present.
 */
function removeRadar(map: maplibregl.Map): void {
  if (map.getLayer(LAYER_ID)) {
    map.removeLayer(LAYER_ID);
  }
  if (map.getSource(SOURCE_ID)) {
    map.removeSource(SOURCE_ID);
  }
}

/**
 * Apply (or update) the RainViewer radar raster overlay on a MapLibre map.
 *
 * - If state has status 'ok' with a valid summary (host + path): adds or
 *   replaces the raster source + layer, inserted below the first symbol
 *   layer so city labels remain on top.
 * - If state is down/missing: removes the layer + source if present.
 *
 * Idempotent: safe to call on every store change.
 * No-ops when the map style is not yet loaded.
 *
 * Frame paths expire (HTTP 410) — the caller must re-invoke whenever the
 * radar LayerState updates so the tile URL refreshes.
 */
export function applyRadar(map: maplibregl.Map, state: LayerState | undefined): void {
  if (!map.isStyleLoaded()) return;

  const summary = state?.status === 'ok' ? state.summary : null;
  const host = typeof summary?.['host'] === 'string' ? summary['host'] : null;
  const path = typeof summary?.['path'] === 'string' ? summary['path'] : null;

  if (!host || !path) {
    removeRadar(map);
    return;
  }

  const tiles = [radarTileUrl(host, path)];

  // Remove old source/layer before re-adding — the frame path changes each poll
  // and MapLibre does not support updating tile URLs on an existing source.
  removeRadar(map);

  map.addSource(SOURCE_ID, {
    type: 'raster',
    tiles,
    tileSize: 256,
    // RainViewer serves real radar only up to ~z7; above that it returns a
    // "Zoom Level Not Supported" placeholder tile. Cap the source so MapLibre
    // requests ≤z7 tiles and overzooms (scales) them for higher map zooms.
    maxzoom: 7,
  });

  const beforeLayerId = firstSymbolLayerId(map);

  map.addLayer(
    {
      id: LAYER_ID,
      type: 'raster',
      source: SOURCE_ID,
      paint: {
        'raster-opacity': RADAR_OPACITY,
      },
    },
    beforeLayerId,
  );
}
